/**
 * Session-Store — XSS-resistente Strategie:
 *   - Access-Token + CSRF-Token: In-Memory (Zustand-Closure). Niemals persistent.
 *   - Refresh-Token: existiert im Frontend GAR NICHT — httpOnly-Cookie `stayon_rt`.
 *
 * Kein sessionStorage/localStorage mehr für Tokens (siehe FRONTEND_GUIDELINES.md §8.1).
 * Persistenz über Browser-Neustart liefert allein das httpOnly-Cookie; die In-Memory-Session
 * wird beim Boot über die Refresh-Pipeline neu aufgebaut.
 *
 * Die `subscribe`-Methode erlaubt es Reactive-Stores (Zustand, TanStack-Query) auf
 * Session-Wechsel zu reagieren, ohne den Store selbst zu kennen.
 */
import type { Session } from './types';

type Listener = (session: Session | null) => void;

export interface TokenStore {
  get: () => Session | null;
  set: (session: Session) => void;
  clear: () => void;
  subscribe: (listener: Listener) => () => void;
}

export function createTokenStore(): TokenStore {
  let inMemory: Session | null = null;
  const listeners = new Set<Listener>();

  function notify(session: Session | null) {
    for (const listener of listeners) {
      listener(session);
    }
  }

  return {
    get: () => inMemory,
    set: (session) => {
      inMemory = session;
      notify(session);
    },
    clear: () => {
      inMemory = null;
      notify(null);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
